import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, KeyboardAvoidingView, Platform, Pressable, ScrollView, Switch, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { guestScanPolicyApi, PolicyScanDraft, scanPolicyApi, uploadPolicyFileApi } from '@/api/mobile-api';
import { useAppMetaStore } from '@/store/app-meta-store';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { DateField } from '@/components/ui/date-field';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useAuthStore } from '@/store/auth-store';
import { usePoliciesStore } from '@/store/policies-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { Policy, PolicyType } from '@/types/domain';

const TYPES: { key: PolicyType; label: string }[] = [
  { key: 'CAR_OC', label: 'OC auto' },
  { key: 'CAR_AC', label: 'AC auto' },
  { key: 'HOME', label: 'Dom / mieszkanie' },
  { key: 'LIFE', label: 'Życie' },
  { key: 'NNW', label: 'NNW' },
  { key: 'TRAVEL', label: 'Podróż' },
  { key: 'HEALTH', label: 'Zdrowie' },
  { key: 'OTHER', label: 'Inna' },
];

function parseZl(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

const VALID_TYPES: PolicyType[] = ['CAR_OC', 'CAR_AC', 'HOME', 'LIFE', 'NNW', 'TRAVEL', 'HEALTH', 'OTHER'];
const VALID_PERIODS: Policy['premiumPeriod'][] = ['ANNUAL', 'SEMI_ANNUAL', 'QUARTERLY', 'MONTHLY', 'ONE_OFF'];

/** Manual policy add — the quick path (a few fields, no scan). Auto-creates an end-date reminder. */
export default function NewPolicyScreen() {
  const { t } = useTranslation();
  const addManualPolicy = usePoliciesStore((s) => s.addManualPolicy);
  const addPolicyDocument = usePoliciesStore((s) => s.addPolicyDocument);
  const account = useAuthStore((s) => s.account);
  const ratingDone = useAppMetaStore((s) => s.ratingDone);
  const policyCloudBackup = useAppMetaStore((s) => s.policyCloudBackup);
  const policyHealthConsent = useAppMetaStore((s) => s.policyHealthConsent);
  const { mode } = useLocalSearchParams<{ mode?: string }>();

  const [type, setType] = useState<PolicyType>('CAR_OC');
  const [insurer, setInsurer] = useState('');
  const [number, setNumber] = useState('');
  const [premiumRaw, setPremiumRaw] = useState('');
  const [startIso, setStartIso] = useState<string | null>(null);
  const [endIso, setEndIso] = useState<string | null>(null);
  const [openEnded, setOpenEnded] = useState(false);
  const [customName, setCustomName] = useState('');
  const [techExamIso, setTechExamIso] = useState<string | null>(null);

  const isMotor = type === 'CAR_OC' || type === 'CAR_AC';

  // AI scan state — the draft keeps the fields the quick form has no inputs for
  // (subject, startDate, premiumPeriod), so saving preserves everything read.
  const [scanning, setScanning] = useState(false);
  const [scanDraft, setScanDraft] = useState<PolicyScanDraft | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  // The scanned file itself — kept until save so the scan becomes the policy's
  // DOCUMENT (owner: "powinien tu być podpięty dokument który wysłałem").
  const [scanFile, setScanFile] = useState<{ uri: string; name: string; mimeType: string } | null>(null);

  const premiumGr = parseZl(premiumRaw);
  const endDate = endIso;
  const valid = insurer.trim().length > 0 && premiumGr > 0 && (openEnded || endDate !== null);

  const applyDraft = (draft: PolicyScanDraft) => {
    if (VALID_TYPES.includes(draft.policyType as PolicyType)) setType(draft.policyType as PolicyType);
    if (draft.insurerName) setInsurer(draft.insurerName);
    if (draft.policyNumber) setNumber(draft.policyNumber);
    if (draft.premiumGr && draft.premiumGr > 0) setPremiumRaw(String(Math.round(draft.premiumGr / 100)));
    if (draft.startDate) setStartIso(draft.startDate);
    if (draft.endDate) {
      setOpenEnded(false);
      setEndIso(draft.endDate);
    }
    setScanDraft(draft);
  };

  // One place to send a picked file (photo OR PDF) to the AI reader. The backend
  // renders a PDF to images itself, so the app only has to forward the bytes.
  // A hard timeout guards against an endless spinner (owner: "kręci się i nie
  // wiem, czy trwa, czy błąd") — a big PDF/slow network surfaces as an error.
  // 1.2.0: GUESTS scan too (the "wow" taster) via the public endpoint, hard-capped
  // server-side at 4/day + 8 lifetime per device — then the message walls them
  // into logging in (logged-in accounts get 10/day).
  const sendScan = async (file: { uri: string; name: string; mimeType: string }) => {
    setScanning(true);
    setScanError(null);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('scan-timeout')), 75000);
    });
    let result: Awaited<ReturnType<typeof scanPolicyApi>>;
    try {
      const scanCall = account ? scanPolicyApi(file) : guestScanPolicyApi(file);
      result = await Promise.race([scanCall, timeout]);
    } catch (e) {
      setScanning(false);
      // Only the 75 s guard rejects with this marker. Anything else is a real
      // failure (e.g. the picked file could not be read) — don't cry "timeout".
      setScanError((e as Error)?.message === 'scan-timeout' ? t('newPolicy.scanTimeout') : t('newPolicy.scanError'));
      return;
    } finally {
      if (timer) clearTimeout(timer);
    }
    setScanning(false);
    if (!result.ok || !result.data) {
      // Surface the transport's real reason (no connection / please log in /
      // AI limit reached / AI temporarily down) rather than a generic line.
      setScanError(result.message ?? t('newPolicy.scanError'));
      return;
    }
    applyDraft(result.data);
    // Remember the file — attached locally on save and (opt-in) uploaded to the
    // cloud AFTER the policy exists, so the backup carries policyClientId.
    // Uploading here (pre-save) left the file unlinked to any policy.
    setScanFile(file);
  };

  const pickAndScan = async (fromCamera: boolean) => {
    setScanError(null);
    if (fromCamera) {
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      if (!perm.granted) return;
    }
    const picked = fromCamera
      ? await ImagePicker.launchCameraAsync({ mediaTypes: ['images'], quality: 0.8 })
      : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.8 });
    if (picked.canceled || !picked.assets?.length) return;
    const asset = picked.assets[0];
    await sendScan({
      uri: asset.uri,
      name: asset.fileName ?? 'polisa.jpg',
      mimeType: asset.mimeType ?? 'image/jpeg',
    });
  };

  // PDF (or image) straight from Files — a scan is often a PDF, not a gallery
  // photo (owner request). Works on web too (document picker = file input).
  const pickPdfAndScan = async () => {
    setScanError(null);
    const picked = await DocumentPicker.getDocumentAsync({
      type: ['application/pdf', 'image/*'],
      copyToCacheDirectory: true,
      multiple: false,
    });
    if (picked.canceled || !picked.assets?.length) return;
    const asset = picked.assets[0];
    await sendScan({
      uri: asset.uri,
      name: asset.name ?? 'polisa.pdf',
      mimeType: asset.mimeType ?? 'application/pdf',
    });
  };

  // "Zeskanuj" tile jumps straight to the camera (owner request). Web has no
  // camera picker, so there it just lands on the form with the gallery button.
  useEffect(() => {
    if (mode === 'scan' && Platform.OS !== 'web') void pickAndScan(true);
    // run once on mount for the scan entry point
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    const scanPeriod = scanDraft?.premiumPeriod as Policy['premiumPeriod'] | null | undefined;
    const policy = await addManualPolicy({
      type,
      customName: customName.trim() || null,
      insurerName: insurer.trim(),
      policyNumber: number.trim() || null,
      premiumGr,
      endDate: openEnded ? null : endDate,
      nextTechnicalExamDate: isMotor ? techExamIso : null,
      source: scanDraft ? 'SCAN' : 'MANUAL',
      subject: scanDraft?.subject ?? null,
      startDate: startIso ?? scanDraft?.startDate ?? null,
      premiumPeriod: scanPeriod && VALID_PERIODS.includes(scanPeriod) ? scanPeriod : undefined,
    });
    if (scanFile) {
      // The scan IS the policy document — attached locally always, regardless
      // of any cloud setting (the previous flow just dropped the file).
      await addPolicyDocument(policy.id, scanFile.uri).catch(() => undefined);
      // Opt-in cloud backup, AFTER the policy exists so the file is linked via
      // policyClientId. RODO gates unchanged: LIFE/HEALTH need art. 9 consent.
      if (account && policyCloudBackup) {
        const isHealth = type === 'LIFE' || type === 'HEALTH';
        if (!isHealth || policyHealthConsent) {
          void uploadPolicyFileApi(scanFile, {
            label: insurer.trim() || undefined,
            policyClientId: policy.id,
            healthConsent: isHealth,
          }).catch(() => undefined);
        }
      }
    }
    // Catch the happy moment: first successful policy add -> ask for a rating
    // (replace, so Wstecz doesn't bounce back into the just-saved form). We only
    // ever prompt once (ratingDone), so returning users just go back.
    if (!ratingDone) router.replace('/rate');
    else router.back();
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
      >
        <ScreenHeader title={t('newPolicy.title')} />

        <AppText variant="bodyMd">{t('newPolicy.subtitle')}</AppText>

        {/* AI scan: photo -> prefilled form. Needs a logged-in account (the
            server endpoint is per-account rate-limited); guests are nudged to
            log in below. The read runs server-side and the photo is never stored. */}
        <Card style={{ gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
            <Ionicons name="sparkles-outline" size={20} color={colors.primary} />
            <AppText variant="headlineSm">{t('newPolicy.scanTitle')}</AppText>
          </View>
          <AppText variant="bodyMd">{t('newPolicy.scanHint')}</AppText>
          {scanning ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 44 }}>
              <ActivityIndicator color={colors.primary} />
              <View style={{ flex: 1 }}>
                <AppText variant="bodyLg">{t('newPolicy.scanReading')}</AppText>
                <AppText variant="labelMd" color={colors.textSecondary}>
                  {t('newPolicy.scanReadingHint')}
                </AppText>
              </View>
            </View>
          ) : (
            <View style={{ gap: spacing.sm }}>
              <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                {Platform.OS !== 'web' && (
                  <PrimaryButton
                    label={t('newPolicy.scanCamera')}
                    onPress={() => pickAndScan(true)}
                    style={{ flex: 1 }}
                  />
                )}
                <PrimaryButton
                  label={t('newPolicy.scanGallery')}
                  onPress={() => pickAndScan(false)}
                  style={{ flex: 1 }}
                />
              </View>
              {/* Second row: PDF / file from storage (not the photo gallery). */}
              <PrimaryButton
                label={t('newPolicy.scanPdf')}
                variant="secondary"
                onPress={pickPdfAndScan}
              />
            </View>
          )}
          {scanDraft && !scanning && (
            <AppText variant="labelMd" color={colors.primary}>
              {t('newPolicy.scanDone')}
            </AppText>
          )}
          {scanDraft?.warning && !scanning && (
            <AppText variant="labelMd" color={colors.warning}>
              {scanDraft.warning}
            </AppText>
          )}
          {scanError && !scanning && (
            <AppText variant="labelMd" color={colors.error}>
              {scanError}
            </AppText>
          )}
          {/* Guests get a smaller daily quota — nudge to log in for more (limit
              enforced server-side once the guest-scan endpoint is live). */}
          {!account && (
            <Pressable accessibilityRole="button" onPress={() => router.push('/auth/login')}>
              <AppText variant="labelMd" color={colors.primary}>
                {t('newPolicy.scanLimitGuest')}
              </AppText>
            </Pressable>
          )}
        </Card>

        {/* Type chips */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
          {TYPES.map(({ key, label }) => {
            const active = type === key;
            return (
              <Pressable
                key={key}
                accessibilityRole="button"
                onPress={() => setType(key)}
                style={{
                  backgroundColor: active ? colors.navy : colors.card,
                  borderRadius: radius.full,
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.xs,
                }}
              >
                <AppText variant="labelLg" color={active ? '#FFFFFF' : colors.navy}>
                  {label}
                </AppText>
              </Pressable>
            );
          })}
        </View>

        <TextField label={t('newPolicy.insurer')} placeholder="np. PZU" value={insurer} onChangeText={setInsurer} />
        <TextField
          label={t('newPolicy.premium')}
          placeholder="np. 1 200"
          keyboardType="numeric"
          value={premiumRaw}
          onChangeText={setPremiumRaw}
        />

        {/* Start of protection — drives the time-progress bar on the card. */}
        <DateField
          label={t('newPolicy.startDate')}
          value={startIso}
          onChange={setStartIso}
        />

        {/* End date OR open-ended */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 44 }}>
          <AppText variant="bodyLg" style={{ flex: 1 }}>
            {t('newPolicy.openEnded')}
          </AppText>
          <Switch
            value={openEnded}
            onValueChange={setOpenEnded}
            trackColor={{ false: colors.disabledBg, true: colors.primary }}
            thumbColor="#FFFFFF"
          />
        </View>
        {!openEnded && (
          <>
            <DateField
              label={t('newPolicy.endDate')}
              value={endIso}
              onChange={setEndIso}
              minIso={new Date().toISOString().slice(0, 10)}
            />
            <AppText variant="labelMd" color={colors.primary}>
              {t('newPolicy.reminderHint')}
            </AppText>
          </>
        )}

        {/* Technical inspection date — motor policies only, feeds a TECH_EXAM reminder. */}
        {isMotor && (
          <>
            <DateField
              label={t('newPolicy.techExamDate')}
              value={techExamIso}
              onChange={setTechExamIso}
              minIso={new Date().toISOString().slice(0, 10)}
            />
            {techExamIso !== null && (
              <AppText variant="labelMd" color={colors.primary}>
                {t('newPolicy.techExamHint')}
              </AppText>
            )}
          </>
        )}

        <TextField
          label={t('newPolicy.number')}
          placeholder={t('newPolicy.numberPlaceholder')}
          value={number}
          onChangeText={setNumber}
          // The scan warning about a suspect number highlights THIS field —
          // before, a misread number sailed through with no visual signal.
          style={
            scanDraft?.warning && scanDraft.warning.includes('numer polisy')
              ? { borderWidth: 1.5, borderColor: colors.warning }
              : undefined
          }
        />
        <TextField
          label={t('newPolicy.customName')}
          placeholder={t('newPolicy.customNamePlaceholder')}
          value={customName}
          onChangeText={setCustomName}
        />

        <PrimaryButton label={t('newPolicy.save')} disabled={!valid} onPress={onSave} />
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
